import { useState, useEffect } from 'react';
import { Key, Plus, Trash2, Bell, Users, Save } from 'lucide-react';
import { getSettings, createApiKey, revokeApiKey, updateSettings } from '../lib/api';
import type { Settings as SettingsType, ApiKey, AdminUser, NotificationPreferences } from '../types';

export default function Settings() {
  const [settings, setSettings] = useState<SettingsType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'api-keys' | 'notifications' | 'users'>('api-keys');
  const [newKeyName, setNewKeyName] = useState('');
  const [createdKey, setCreatedKey] = useState('');
  const [notifications, setNotifications] = useState<NotificationPreferences>({
    emailNotifications: true,
    uploadAlerts: true,
    seoAlerts: true,
    orderAlerts: true,
    systemAlerts: true,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  async function fetchSettings(): Promise<void> {
    try {
      const data = await getSettings();
      setSettings(data);
      setNotifications(data.notifications);
    } catch {
      setSettings(null);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateKey(): Promise<void> {
    if (!newKeyName.trim()) return;
    try {
      const result = await createApiKey(newKeyName);
      setCreatedKey(result.key);
      setNewKeyName('');
      await fetchSettings();
    } catch {
      void 0;
    }
  }

  async function handleRevokeKey(keyId: string): Promise<void> {
    try {
      await revokeApiKey(keyId);
      await fetchSettings();
    } catch {
      void 0;
    }
  }

  async function handleSaveNotifications(): Promise<void> {
    setIsSaving(true);
    try {
      await updateSettings({ notifications });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch {
      void 0;
    } finally {
      setIsSaving(false);
    }
  }

  function handleNotificationChange(key: keyof NotificationPreferences): void {
    setNotifications((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleToggleUser(user: AdminUser): Promise<void> {
    try {
      await updateSettings({
        adminUsers: (settings?.adminUsers ?? []).map((u) =>
          u.id === user.id ? { ...u, isActive: !u.isActive } : u
        ),
      });
      await fetchSettings();
    } catch {
      void 0;
    }
  }

  const tabs = [
    { id: 'api-keys' as const, label: 'API Keys', icon: Key },
    { id: 'notifications' as const, label: 'Notifications', icon: Bell },
    { id: 'users' as const, label: 'Users', icon: Users },
  ];

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-sm text-muted-foreground">Manage API keys, notifications, and users</p>
      </div>

      <div className="flex gap-2 border-b border-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <tab.icon className="h-4 w-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'api-keys' && (
        <div className="card">
          <h2 className="text-lg font-semibold text-foreground">API Keys</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Generate and manage API keys for external integrations.
          </p>

          <div className="mt-4 flex gap-2">
            <input
              type="text"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              placeholder="Key name (e.g., SEO Agent)"
              className="input-field flex-1"
            />
            <button onClick={handleCreateKey} className="btn-primary">
              <Plus className="mr-1.5 h-4 w-4" />
              Generate
            </button>
          </div>

          {createdKey && (
            <div className="mt-4 rounded-lg bg-success/10 p-3">
              <p className="text-sm font-medium text-success">Key created successfully!</p>
              <code className="mt-1 block rounded bg-muted px-2 py-1 text-xs font-mono text-foreground break-all">
                {createdKey}
              </code>
              <p className="mt-1 text-xs text-muted-foreground">
                Copy this key now. It will not be shown again.
              </p>
            </div>
          )}

          <div className="mt-6 space-y-3">
            {settings?.apiKeys.map((key) => (
              <div
                key={key.id}
                className="flex items-center justify-between rounded-lg border border-border p-3"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{key.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Created: {new Date(key.createdAt).toLocaleDateString()}
                    {key.expiresAt && ` | Expires: ${new Date(key.expiresAt).toLocaleDateString()}`}
                  </p>
                  <span className={`mt-1 inline-block text-xs ${key.isActive ? 'text-success' : 'text-muted-foreground'}`}>
                    {key.isActive ? 'Active' : 'Revoked'}
                  </span>
                </div>
                {key.isActive && (
                  <button
                    onClick={() => handleRevokeKey(key.id)}
                    className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10"
                    title="Revoke"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {(!settings?.apiKeys || settings.apiKeys.length === 0) && (
              <p className="py-8 text-center text-sm text-muted-foreground">No API keys generated yet.</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Notification Preferences</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose which notifications you want to receive.
              </p>
            </div>
            {saveSuccess && (
              <span className="badge badge-success">Saved</span>
            )}
          </div>

          <div className="mt-6 space-y-4">
            {(Object.keys(notifications) as Array<keyof NotificationPreferences>).map((key) => (
              <div key={key} className="flex items-center justify-between">
                <label htmlFor={key} className="text-sm font-medium text-foreground capitalize">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <button
                  id={key}
                  onClick={() => handleNotificationChange(key)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${
                    notifications[key] ? 'bg-primary-600' : 'bg-muted'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${
                      notifications[key] ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <button onClick={handleSaveNotifications} disabled={isSaving} className="btn-primary">
              <Save className="mr-1.5 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="card">
          <h2 className="text-lg font-semibold text-foreground">Admin Users</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage admin user accounts and permissions.
          </p>

          <div className="mt-6 space-y-3">
            {settings?.adminUsers.map((user) => (
              <div
                key={user.id}
                className="flex items-center justify-between rounded-lg border border-border p-3"
              >
                <div>
                  <p className="text-sm font-medium text-foreground">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                  <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground capitalize">
                    {user.role.replace('_', ' ')}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs ${user.isActive ? 'text-success' : 'text-muted-foreground'}`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={() => handleToggleUser(user)}
                    className={`rounded-lg px-2 py-1 text-xs font-medium transition-colors ${
                      user.isActive
                        ? 'text-destructive hover:bg-destructive/10'
                        : 'text-success hover:bg-success/10'
                    }`}
                  >
                    {user.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            ))}
            {(!settings?.adminUsers || settings.adminUsers.length === 0) && (
              <p className="py-8 text-center text-sm text-muted-foreground">No admin users found.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
